import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import api from '../../services/api';

interface Option {
  id: number;
  text: string;
  is_correct: boolean;
}

interface Stimulus {
  id: number;
  image_path: string;
  correct_answer: string;
  options: Option[];
}

interface Exercise {
  id: number;
  title: string;
  type: 'word_selection' | 'gender_selection';
  stimuli: Stimulus[];
}

type FeedbackState = 'correct' | 'incorrect' | null;

export default function HerramientasSessionPage() {
  const { id } = useParams<{ id: string }>();
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [feedback, setFeedback] = useState<FeedbackState>(null);
  const [finished, setFinished] = useState(false);
  const [correct, setCorrect] = useState(0);

  useEffect(() => {
    api.get(`/rehab/exercises/${id}`)
      .then(res => {
        if (res.data.ok) setExercise(res.data.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const currentStimulus = exercise?.stimuli[currentIndex];
  const total = exercise?.stimuli.length ?? 0;

  const handleAnswer = (option: Option) => {
    if (feedback !== null) return;

    if (option.is_correct) {
      setFeedback('correct');
      setCorrect(prev => prev + 1);
      // Auto-advance after 1.2s
      setTimeout(() => {
        advance();
      }, 1200);
    } else {
      setFeedback('incorrect');
    }
  };

  const advance = () => {
    setFeedback(null);
    if (currentIndex + 1 >= total) {
      setFinished(true);
    } else {
      setCurrentIndex(prev => prev + 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neureka-yellow"></div>
      </div>
    );
  }

  if (!exercise) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-4">
        <p className="text-white/70">Ejercicio no encontrado</p>
        <Link to="/herramientas" className="text-neureka-yellow hover:underline">Volver</Link>
      </div>
    );
  }

  if (finished) {
    const pct = Math.round((correct / total) * 100);
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="min-h-screen flex items-center justify-center px-4"
      >
        <div className="glass-container text-center max-w-sm w-full py-12">
          <div className="text-6xl mb-4">
            {pct >= 80 ? '🎉' : pct >= 50 ? '👍' : '💪'}
          </div>
          <h2 className="text-2xl font-display font-bold mb-2">¡Ejercicio completado!</h2>
          <p className="text-white/70 mb-6">
            {correct} de {total} correctas ({pct}%)
          </p>
          <div className="flex gap-3 justify-center">
            <Link
              to="/herramientas"
              className="px-5 py-2.5 border border-white/20 rounded-lg text-white/70 hover:text-white transition-colors"
            >
              Volver
            </Link>
            <button
              onClick={() => {
                setCurrentIndex(0);
                setFeedback(null);
                setFinished(false);
                setCorrect(0);
              }}
              className="px-5 py-2.5 bg-neureka-yellow text-neureka-dark font-semibold rounded-lg hover:bg-yellow-400 transition-colors"
            >
              Repetir
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <Link to="/herramientas" className="text-white/60 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="text-sm text-white/60">
          {exercise.title} — {currentIndex + 1} / {total}
        </div>
        <div className="w-5" />
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-white/10">
        <motion.div
          className="h-full bg-neureka-yellow"
          animate={{ width: `${((currentIndex + 1) / total) * 100}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Stimulus */}
      <AnimatePresence mode="wait">
        {currentStimulus && (
          <motion.div
            key={currentStimulus.id}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            className="flex-1 flex flex-col lg:flex-row items-center justify-center gap-8 px-4 py-8"
          >
            {/* Image */}
            <div className="flex-shrink-0">
              <img
                src={currentStimulus.image_path}
                alt="estímulo"
                className="w-64 h-64 object-contain rounded-2xl bg-white/5 p-4"
              />
            </div>

            {/* Options */}
            <div className={`grid gap-4 w-full max-w-xs ${exercise.type === 'gender_selection' ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {currentStimulus.options.map(option => {
                const isSelected = feedback !== null;
                const isCorrectOpt = option.is_correct;
                let optClass = 'bg-white/5 hover:bg-white/10 border-white/20 hover:border-white/40 text-white';

                if (isSelected) {
                  if (isCorrectOpt) {
                    optClass = 'bg-green-500/20 border-green-400 text-green-300';
                  } else if (feedback === 'incorrect' && !isCorrectOpt) {
                    optClass = 'bg-red-500/10 border-red-400/30 text-white/50';
                  }
                }

                return (
                  <button
                    key={option.id}
                    onClick={() => handleAnswer(option)}
                    disabled={feedback !== null}
                    className={`py-4 px-6 text-xl font-bold rounded-2xl border-2 transition-colors ${optClass} disabled:cursor-not-allowed`}
                  >
                    {option.text}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Feedback overlay */}
      <AnimatePresence>
        {feedback === 'correct' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3 px-6 py-4 rounded-2xl bg-green-500/90 text-white font-bold text-lg shadow-xl"
          >
            <CheckCircle className="w-6 h-6" />
            CORRECTO
          </motion.div>
        )}

        {feedback === 'incorrect' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 px-6 py-4 rounded-2xl bg-red-500/90 text-white shadow-xl"
          >
            <div className="flex items-center gap-2 font-bold text-lg">
              <XCircle className="w-6 h-6" />
              INCORRECTO
            </div>
            <button
              onClick={advance}
              className="text-sm underline opacity-80 hover:opacity-100"
            >
              Siguiente
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
